import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api"; // 👈 Your axios instance

const PaymentResult = () => {
  const query = new URLSearchParams(useLocation().search);
  const status = query.get("status");
  const bookingId = query.get("bookingId");
  const orderId = query.get("orderId");
  // const pdfpath = query.get("pdfpath");
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true); // Start with validation
  const [retrying, setRetrying] = useState(false); // Spinner for retry
  const [validBooking, setValidBooking] = useState(false);

  // Validate bookingId + orderId with backend
  useEffect(() => {
    const validateBooking = async () => {
      if (!bookingId) {
        setLoading(false);
        setValidBooking(false);
        return;
      }

      try {
        const res = await api.get(`validate/${bookingId}`);

        if (res.data?.valid) {
          // If orderId present, ensure it matches
          if (orderId && res.data.order_id && res.data.order_id !== orderId) {
            setValidBooking(false);
          } else {
            setValidBooking(true);
          }
        } else {
          setValidBooking(false);
        }
      } catch (err) {
        console.error("Validation error:", err);
        setValidBooking(false);
      } finally {
        setLoading(false);
      }
    };

    validateBooking();
  }, [bookingId, orderId]);

  const handleGoHome = () => {
    navigate("/"); // Adjust route if your homepage differs
  };

  const handleRetryPayment = async () => {
    try {
      setRetrying(true);

      const res = await api.post("retry_booking", { bookingId });

      if (res.data?.checkoutUrl) {
        window.location.href = res.data.checkoutUrl; // Redirect to Stripe checkout
      } else {
        alert("Unable to retry payment. Please try again later.");
        setRetrying(false);
      }
    } catch (err) {
      console.error(err);
      alert("Error retrying payment.");
      setRetrying(false);
    }
  };

  // Fullscreen overlay spinner (initial loading)
  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div className="flex flex-col items-center">
          <svg
            className="animate-spin h-12 w-12 text-white mb-3"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            ></path>
          </svg>
          <p className="text-white text-lg font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // Invalid or tampered URL
  if (!validBooking) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
        <div className="bg-white shadow-lg rounded-2xl p-8 text-center max-w-md w-full py-5">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            ❌ Invalid Link
          </h2>
          <p className="text-gray-700 mb-4">
            The booking ID or order ID provided is invalid or has been tampered
            with.
          </p>
          <button
            onClick={handleGoHome}
            className="mt-4 px-6 py-2 btn btn-primary"
          >
            Go Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Valid booking - show success/cancel/failure
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
      <div className="bg-white shadow-lg rounded-2xl p-8 text-center max-w-md w-full py-5">
        {status === "success" ? (
          <>
            <h2 className="text-2xl font-bold text-green-600 mb-4">
              ✅ Payment Successful!
            </h2>
            <p className="text-gray-700">
              Your booking <strong>#{bookingId}</strong> has been confirmed.
            </p>
            <p className="text-gray-700 mb-6">
              Order ID: <strong>{orderId}</strong>
            </p>
            {/* <p className="text-gray-700 mb-6">
              Download Invoice: <strong>{pdfpath}</strong>
            </p> */}
          </>
        ) : status === "cancel" ? (
          <>
            <h2 className="text-2xl font-bold text-yellow-600 mb-4">
              ⚠️ Payment Cancelled
            </h2>
            <p className="text-gray-700">
              Your booking <strong>#{bookingId}</strong> was not completed.
            </p>
            <p className="text-gray-600 mb-6">You can try again anytime.</p>
            <button
              onClick={handleRetryPayment}
              disabled={retrying}
              className="mt-2 px-6 py-2 bg-yellow-600 hover:bg-yellow-700 text-white font-medium rounded-lg shadow transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {retrying ? "Retrying..." : "Retry Payment"}
            </button>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-red-600 mb-4">
              ❌ Payment Failed
            </h2>
            <p className="text-gray-700">
              Something went wrong with your booking{" "}
              <strong>#{bookingId}</strong>.
            </p>
            <p className="text-gray-600 mb-6">Please try again later.</p>
            <button
              onClick={handleRetryPayment}
              disabled={retrying}
              className="mt-2 px-6 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg shadow transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {retrying ? "Retrying..." : "Retry Payment"}
            </button>
          </>
        )}

        {/* Always show Go Home button */}
        <button onClick={handleGoHome} className="mt-6 px-6 py-2 btn-primary">
          Go Back to Home
        </button>
      </div>
    </div>
  );
};

export default PaymentResult;
